import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCondoContext } from '@/features/auth/use-condo-context';
import {
  useUnidad,
  useHistorialUnidad,
  useDesactivarUnidad,
} from '@/features/unidades/api';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { formatLongDate } from '@/lib/format/date';

export function UnidadDetailRoute() {
  const { unidadId } = useParams();
  const { condoId: tenantId } = useCondoContext();
  const { data, isLoading } = useUnidad(tenantId, unidadId);
  const historialQ = useHistorialUnidad(tenantId, unidadId);
  const desactivar = useDesactivarUnidad(tenantId, unidadId ?? '');
  const [desactivarOpen, setDesactivarOpen] = useState(false);

  if (isLoading || !data) {
    return <PageContainer width="wide">Cargando…</PageContainer>;
  }
  const u = data.unidad;

  return (
    <PageContainer width="wide">
      <Button variant="ghost" asChild size="sm" className="mb-6">
        <Link to={`/app/condominios/${tenantId}/unidades`}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Link>
      </Button>
      <PageHeader
        eyebrow={u.tipo}
        title={`Unidad ${u.numero}`}
        action={
          <div className="flex items-center gap-2">
            <Badge variant={u.activo ? 'success' : 'neutral'}>
              {u.activo ? 'Activa' : 'Inactiva'}
            </Badge>
            {u.activo && (
              <Button size="sm" variant="ghost" onClick={() => setDesactivarOpen(true)}>
                Desactivar
              </Button>
            )}
          </div>
        }
        ruleSpacing="mb-8"
      />

      <dl className="grid grid-cols-4 gap-6 text-sm">
        <div>
          <dt className="eyebrow">m²</dt>
          <dd className="mt-1">{u.m2}</dd>
        </div>
        <div>
          <dt className="eyebrow">Alícuota</dt>
          <dd className="mt-1">{(u.alicuota * 100).toFixed(3)}%</dd>
        </div>
        <div>
          <dt className="eyebrow">Piso</dt>
          <dd className="mt-1">{u.piso ?? '—'}</dd>
        </div>
        <div>
          <dt className="eyebrow">Dormitorios</dt>
          <dd className="mt-1">{u.dormitorios ?? '—'}</dd>
        </div>
      </dl>

      <section className="mt-10">
        <h2 className="font-display text-xl font-semibold">Residentes actuales</h2>
        <ul className="mt-4 space-y-2">
          {data.residentesActivos.length === 0 && (
            <li className="text-sm text-[var(--color-ink-muted)]">
              Sin residentes activos.
            </li>
          )}
          {data.residentesActivos.map((l) => (
            <li
              key={l.residenteId}
              className="flex items-center justify-between rounded-md border px-4 py-3"
            >
              <div>
                <p className="text-sm font-semibold">
                  Residente …{l.residenteId.slice(-6)}
                </p>
                <p className="text-[0.6875rem] text-[var(--color-ink-muted)]">
                  Desde {formatLongDate(l.fechaInicio)} ·{' '}
                  {l.habita ? 'habita' : 'no habita'}
                </p>
              </div>
              <Badge variant={l.rol === 'propietario' ? 'primary' : 'accent'}>
                {l.rol}
              </Badge>
            </li>
          ))}
        </ul>
      </section>

      {(u.tipo === 'vivienda' || u.tipo === 'estacionamiento' || u.tipo === 'bodega') && (
        <section className="mt-10">
          <h2 className="font-display text-xl font-semibold">
            {u.tipo === 'vivienda' ? 'Accesorios vinculados' : 'Vinculada a'}
          </h2>
          <ul className="mt-4 space-y-2">
            {data.vinculosActivos.length === 0 && (
              <li className="text-sm text-[var(--color-ink-muted)]">
                Sin vínculos activos.
              </li>
            )}
            {data.vinculosActivos.map((v) => (
              <li
                key={v.id}
                className="flex items-center justify-between rounded-md border px-4 py-3"
              >
                <div>
                  <p className="text-sm font-semibold">
                    {u.tipo === 'vivienda'
                      ? `Accesorio …${v.accesorioId.slice(-6)}`
                      : `Vivienda …${v.viviendaId.slice(-6)}`}
                  </p>
                  <p className="text-[0.6875rem] text-[var(--color-ink-muted)]">
                    Desde {formatLongDate(v.fechaInicio)}
                    {v.fechaFin && ` → ${formatLongDate(v.fechaFin)}`}
                  </p>
                </div>
                {v.fechaFin === null && (
                  <Badge variant="success">Activo</Badge>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-10">
        <h2 className="font-display text-xl font-semibold">Historial de ocupantes</h2>
        <ul className="mt-4 space-y-2">
          {historialQ.data?.items.length === 0 && (
            <li className="text-sm text-[var(--color-ink-muted)]">Sin historial.</li>
          )}
          {historialQ.data?.items.map((item) => (
            <li
              key={`${item.asignacion.residenteId}-${item.asignacion.fechaInicio}`}
              className="rounded-md border px-4 py-3 text-sm"
            >
              <p className="font-semibold">
                {item.residente.nombre} {item.residente.apellidos}{' '}
                <span className="font-normal text-[var(--color-ink-muted)]">
                  ({item.asignacion.rol})
                </span>
              </p>
              <p className="text-[0.6875rem] text-[var(--color-ink-muted)]">
                {formatLongDate(item.asignacion.fechaInicio)}
                {' → '}
                {item.asignacion.fechaFin
                  ? formatLongDate(item.asignacion.fechaFin)
                  : 'actual'}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <ConfirmDialog
        open={desactivarOpen}
        onOpenChange={setDesactivarOpen}
        title="Desactivar unidad"
        description="Se marcará como inactiva. Requiere que no tenga residentes activos."
        variant="destructive"
        confirmLabel="Desactivar"
        loading={desactivar.isPending}
        onConfirm={() => {
          desactivar.mutate(undefined, {
            onSuccess: () => setDesactivarOpen(false),
          });
        }}
      />
    </PageContainer>
  );
}
